import type { CSSProperties } from 'react';
import { useT } from '../i18n/index.ts';

/**
 * Low-level skeleton placeholder — Dispatch Deck restyle (epic #897).
 * Renders a pulsing block on the `well` token so it reads as a "one
 * level sunken" placeholder against a `card` surface (depth from tonal
 * shift, not shadows).
 *
 * Tailwind's built-in `animate-pulse` (NOT the 1.5s `animate-lamp-pulse`
 * reserved for live-state lamps) is the right fit here: slower, neutral
 * fade that signals "content loading" without competing with any live
 * indicator.
 *
 * Formerly instantiated from the console-shared factory; now a local
 * component so the tenant console owns its own visual language.
 */
interface Props {
  readonly className?: string;
  readonly style?: CSSProperties;
  readonly testId?: string;
}

export function Skeleton({ className = '', style, testId }: Props): JSX.Element {
  return (
    <div
      aria-hidden="true"
      data-testid={testId}
      style={style}
      className={`animate-pulse rounded-lg bg-well ${className}`.trim()}
    />
  );
}

/**
 * Repeating row skeleton for async table loading states. Mirrors the
 * table row cadence so the placeholder doesn't reflow once real data
 * arrives. One sr-only announcement per loading state.
 */
interface TableSkeletonProps {
  readonly rows?: number;
  readonly rowHeight?: number;
  readonly testId?: string;
}

export function TableSkeleton({
  rows = 4,
  rowHeight = 48,
  testId,
}: TableSkeletonProps): JSX.Element {
  const t = useT();
  return (
    <div className="flex flex-col gap-2" data-testid={testId}>
      {Array.from({ length: rows }).map((_, i) => (
        <Skeleton key={i} style={{ height: rowHeight }} />
      ))}
      <span className="sr-only">{t('skeleton.loading')}</span>
    </div>
  );
}
